import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/Button';
import { useModal } from '../../../components/Modal/useModal';
import { Column, Table } from '../../../components/Table/Table';
import { formatNumber } from '../../../core/helpers';
import ProductDetailsModal from './ProductDetailsModal';

export interface InventoryItem {
  asin: string;
  sku: string;
  name: string;
  vat_rate: string;
  customs_number: number;
  weight: number;
  price?: { amount: number; currency: string };
  id: number;
}

interface Props {
  items: InventoryItem[];
  setItems: React.Dispatch<React.SetStateAction<InventoryItem[]>>;
}

export default function InventoryTable({ items, setItems }: Props) {
  const { t } = useTranslation();
  const onSubmit = (item: InventoryItem) => {
    setItems((s) =>
      s.map((f) => {
        if (f.id === item.id) {
          return item;
        }
        return f;
      })
    );
  };

  const { isModalOpened, closeModal, openModal } = useModal();

  const [activeItem, setActiveItem] = useState<InventoryItem>({
    asin: 'B08XY1VHJX',
    sku: '50365',
    name: 'SOFIA MILANI - Damen some long long name',
    vat_rate: 'DE 19%, AT 20%',
    customs_number: 31312312321,
    weight: 1,
    id: 1,
  });

  const columns: Column<InventoryItem>[] = [
    {
      label: 'ASIN',
      field: 'asin',
      maxWidth: '140px',
    },
    {
      label: 'SKU',
      field: 'sku',
      maxWidth: '100px',
    },
    {
      label: 'Name',
      field: 'name',
      formatter: (item) => <div className='text-truncate'>{item.name}</div>,
      maxWidth: '1.15fr',
    },
    {
      label: 'VAT Rate',
      field: 'vat_rate',
      formatter: (item) => <div className='text-truncate'>{item.vat_rate}</div>,
      minWidth: 150,
    },
    {
      label: 'Customs Number',
      field: 'customs_number',
      minWidth: 154,
    },
    {
      label: 'Weight',
      field: 'weight',
      maxWidth: '130px',
    },
    {
      label: 'Purchase price',
      field: 'price',
      formatter: (item) => (
        <div>
          {item.price ? (
            formatNumber(item.price.amount / 100, {
              currency: item.price.currency,
            })
          ) : (
            <Button
              styling='naked-blue'
              className='p-0 border-0 bg-transparent transition-color'
              onClick={() => {
                setActiveItem(item);
                openModal();
              }}
            >
              +Add
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className='Text--20 fw-800 mb-3'>
        {t('*number* Products without purchase price').replace(
          '*number*',
          String(3)
        )}
      </div>
      <Table<InventoryItem>
        columns={columns}
        data={items}
        className='Table--Inventory'
      />
      <ProductDetailsModal
        closeModal={closeModal}
        isModalOpened={isModalOpened}
        item={activeItem}
        onSubmit={onSubmit}
      />
    </>
  );
}

export const INVENTORY_ITEMS: InventoryItem[] = [
  {
    asin: 'B08XY1VHJX',
    sku: '50365',
    name: 'SOFIA MILANI - Damen',
    vat_rate: 'DE 19%, AT 20%',
    customs_number: 31312312321,
    weight: 1,
    id: 1,
  },
  {
    asin: 'B08XY1VHJX',
    sku: '50365',
    name: 'Vintage boots made in France',
    vat_rate: 'DE 19%, AT 20%',
    customs_number: 31312312321,
    weight: 1,
    id: 2,
  },
  {
    asin: 'B08XY1VHJX',
    sku: '50365',
    name: 'Armchair for barbershop...',
    vat_rate: 'DE 19%, AT 20%',
    customs_number: 31312312321,
    weight: 1,
    id: 3,
  },
  {
    asin: 'B08XY1VHJX',
    sku: '50365',
    name: 'SOFIA MILANI - Damen',
    vat_rate: 'DE 19%, AT 20%',
    customs_number: 31312312321,
    weight: 1,
    id: 4,
    price: {
      amount: 469,
      currency: 'eur',
    },
  },
  ...Array(20)
    .fill({
      asin: 'B08XY1VHJX',
      sku: '50365',
      name: 'Vintage boots made in France',
      vat_rate: 'DE 19%, AT 20%',
      customs_number: 31312312321,
      weight: 1,
      id: 5,
      price: {
        amount: 6600,
        currency: 'eur',
      },
    })
    .map((v, i) => ({
      ...v,
      id: v.id + i,
      price: (v.id + i) % 3 !== 0 ? v.price : undefined,
    })),
  {
    asin: 'B08XY1VHJX',
    sku: '50365',
    name: 'SOFIA MILANI - Damen',
    vat_rate: 'DE 19%, AT 20%',
    customs_number: 31312312321,
    weight: 1,
    id: 26,
    price: {
      amount: 40000,
      currency: 'eur',
    },
  },
];

export const inventoryfilterFn = (str: string) => (c: InventoryItem) => {
  return ['name', 'asin', 'sku', 'customs_number'].some((f) =>
    String(c[f as keyof InventoryItem])
      .toLowerCase()
      .includes(str.toLowerCase().trim())
  );
};
